import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { classNames, Icon } from '@pella/sash-framework';
import { CTA, Image } from '../../../';
import { isValidArray } from '../../../utilities/validations';
import HoverModal from '../modal';

const HoverBannerSmall = ({ className }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { hover_cards } = useStaticQuery(query).contentstackHoverConfig;
	const hover = hover_cards.find((hover) => hover.placement.includes('general'));
	const videoRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(hover?.file?.url ? true : false);

	if (!hover) return null;

	const classes = classNames({
		'hover-banner-small grid bg-[#685f5f]/50 md:gap-x-5 xl:gap-x-4 md:grid-cols-2 xl:pr-4 md:max-xl:p-4 text-center md:text-left': true,
		[className]: className,
	});

	const onModalTriggerClick = (event) => {
		event.preventDefault();
		setIsModalOpen(!isModalOpen);
	};

	const onVideControlClick = (event) => {
		event.preventDefault();

		if (!videoRef.current.paused) {
			videoRef.current.pause();
			setIsPlaying(false);
		} else {
			videoRef.current.play();
			setIsPlaying(true);
		}
	};

	return (
		<div
			className={classes}
			data-testid='hover-banner-small'
		>
			{isValidArray(hover.image) && (
				<div className='hover-banner-image-wrapper col-start-1 row-start-3 bg-black/40 p-8 text-center md:row-start-1 md:p-4 xl:row-span-4 xl:px-8 xl:py-6'>
					<Image
						data={hover.image[0]}
						className='hover-banner-image mx-auto aspect-[4/3] max-w-full rounded-[0.625rem]'
					/>
				</div>
			)}

			{hover.file?.url && (
				<div className='hover-banner-video-wrapper relative col-start-1 row-start-3 aspect-[1.43] w-full object-cover md:row-start-1 xl:row-span-4'>
					<video
						data-testid='hover-banner-video'
						className='hover-banner-video aspect-[1.43] w-full object-cover'
						autoPlay
						muted
						loop
						playsInline
						ref={videoRef}
					>
						<source
							src={hover.file.url}
							type='video/mp4'
						/>
						Your browser does not support the video tag.
					</video>

					<button
						onClick={onVideControlClick}
						className='absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center bg-black/80'
						aria-label={isPlaying ? 'Pause video' : 'Play video'}
					>
						<Icon
							name={isPlaying ? 'pause' : 'play_arrow'}
							className='text-white'
							fill={1}
							size={18}
						/>
					</button>
				</div>
			)}

			{hover.heading && <h3 className='hover-banner-heading text-subtitle col-start-1 row-start-1 mx-auto !mb-0 max-w-[20ch] self-center text-pretty pt-6 text-center font-normal md:max-xl:text-h3 md:col-start-2 md:pt-0 md:text-left xl:row-start-2'>{hover.heading}</h3>}

			{isValidArray(hover.cta) && (
				<div className='hover-banner-cta-wrapper col-start-1 row-start-2 mb-6 mt-4 md:col-span-2 md:mb-0 xl:col-span-1 xl:col-start-2 xl:row-start-3'>
					<CTA
						data={hover.cta[0]}
						size='sm'
						onClick={onModalTriggerClick}
						className='!w-fit sm:!w-full'
					/>
				</div>
			)}

			<HoverModal
				defaultOpen={isModalOpen}
				onClose={onModalTriggerClick}
			/>
		</div>
	);
};

HoverBannerSmall.propTypes = {
	className: PropTypes.string,
};

export default HoverBannerSmall;

export const query = graphql`
	{
		contentstackHoverConfig {
			hover_cards {
				placement
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
				file {
					url
				}
				heading
				subheading
				cta {
					...CTA
				}
			}
		}
	}
`;
